import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { reactive, ref, watch } from 'vue';

import {
  validateBirthday,
  validateCnpj,
  validateCpf,
  validateEmail,
  validatePhone,
} from '@/helpers/validators';

import { CriarUsuario } from '@/models/CriarUsuario';
import { LoginType } from '@/models/LoginType';
import { WrappedType } from '@/models/UtilityTypes';

dayjs.extend(customParseFormat);

// == Types =====================================

export interface CadastroForm {
  login: string;
  dataNascimento: string;
  telefoneCelular: string;
  email: string;
  termosCondicoes: boolean;
}

export type CadastroField = keyof CadastroForm;
export type CadastroRecord<T> = Record<CadastroField, T>;

export interface CadastroFormData {
  data: CadastroForm;
  type: LoginType;
  errors: CadastroRecord<string>;
  readonly hasError: boolean;
  validators: CadastroRecord<() => boolean>;
  validate: () => void;
  validateField: CadastroRecord<() => boolean>;
  clearErrors: () => void;
  getUsuarioDto: (nome?: string) => CriarUsuario;

  reset: () => void;
}

// == Default values ============================

const defaultForm: CadastroForm = {
  login: '',
  dataNascimento: '',
  telefoneCelular: '',
  email: '',
  termosCondicoes: false,
};

const defaultErrors: CadastroRecord<string> = {
  login: '',
  dataNascimento: '',
  telefoneCelular: '',
  email: '',
  termosCondicoes: '',
};

const errorMsgKeys: CadastroRecord<string | Record<LoginType, string>> = {
  login: {
    cpf: 'erros.cpfInvalido',
    cnpj: 'erros.cnpjInvalido',
  },
  dataNascimento: {
    cpf: 'erros.dataNascInvalida',
    cnpj: 'erros.dataAberturaInvalida',
  },
  telefoneCelular: 'erros.celularInvalido',
  email: 'erros.emailInvalido',
  termosCondicoes: 'erros.termosNaoAceitos',
};

// == Implementação =============================

const loginValidators: Record<LoginType, (val: string) => boolean> = {
  cpf: validateCpf,
  cnpj: validateCnpj,
};

export default function useCadastroForm(
  initData?: Partial<WrappedType<CadastroForm>>,
  formType: LoginType = 'cpf',
): CadastroFormData {
  const formData: CadastroForm = reactive({ ...defaultForm, ...initData });
  const formErr: CadastroRecord<string> = reactive({ ...defaultErrors });
  const formTypeRef = ref(formType);

  const cadastroFields: Array<CadastroField> = Object.keys(formData) as Array<CadastroField>;

  cadastroFields
    .forEach((k) => {
      watch(
        () => formData[k],
        () => {
          formErr[k] = '';
        },
      );
    });

  function getErrorMsgKey(key: CadastroField): string {
    const errorMsgKey = errorMsgKeys[key];
    const i18nKey = typeof errorMsgKey === 'string' ? errorMsgKey : errorMsgKey[formTypeRef.value];
    return i18nKey;
  }

  const validators: CadastroFormData['validators'] = {
    login: () => loginValidators[formTypeRef.value](formData.login),
    dataNascimento: () => validateBirthday(formData.dataNascimento),
    telefoneCelular: () => validatePhone(formData.telefoneCelular),
    email: () => validateEmail(formData.email),
    termosCondicoes: () => formData.termosCondicoes,
  };

  const validateField: CadastroFormData['validateField'] = (() => {
    const makeValidator = (key: CadastroField) => () => {
      const result = validators[key]();
      formErr[key] = result ? '' : getErrorMsgKey(key);
      return result;
    };

    const entries = cadastroFields.map((key) => [key, makeValidator(key)]);
    return Object.fromEntries(entries);
  })();

  const validate: CadastroFormData['validate'] = () => {
    cadastroFields.forEach((key) => validateField[key]());
  };

  const clearErrors: CadastroFormData['clearErrors'] = () => {
    cadastroFields.forEach((k) => {
      formErr[k] = '';
    });
  };

  const getUsuarioDto: CadastroFormData['getUsuarioDto'] = (nome?: string) => ({
    login: formData.login,
    dataNascimento: dayjs(formData.dataNascimento, 'DD/MM/YYYY').toDate(),
    telefoneCelular: formData.telefoneCelular,
    email: formData.email,
    cadastroManual: nome !== undefined,
    nome,
  });

  const reset = () => {
    Object.assign(formData, defaultForm);
    Object.assign(formErr, defaultErrors);
  };

  return reactive({
    data: formData,
    errors: formErr,
    type: formTypeRef,

    validators,
    validateField,

    validate,
    clearErrors,
    getUsuarioDto,
    reset,

    get hasError() {
      return Object.values(formErr).some((v) => v);
    },
  });
}
